import Controller from '@ember/controller';
import { action } from '@ember/object';
import { Periods, ALL_CHANNELS } from 'cio/utils/metrics/common';
import {
  ALL_DATA_TYPES,
  AnalysisChannel,
  AnalysisDataType,
  MEASURES as ANALYSIS_MEASURE_ORDER,
} from 'cio/utils/metrics/analysis';
import { inject as service } from '@ember/service';
import EnvironmentService from 'cio/services/environment';
import { ActionType } from 'cio/models/action';
import { actionTypeName } from 'cio/utils/action-info';
import { SortDirection } from 'cio/utils/sort';
import { Grouping } from 'cio/utils/metrics/resolution';
import { isDeliveryMeasureEnabledForChannel } from 'cio/utils/metrics/measure';

const DEFAULT_DATA_TYPES = ['campaign'];
const DEFAULT_CHANNELS: string[] = [];

const ALL_MEASURES = [...ANALYSIS_MEASURE_ORDER];

const DEFAULT_SORT_BY = 'campaign_name';
const DEFAULT_SORT_DIRECTION = SortDirection.Ascending;

const DEFAULT_TAB = 'table';

const DEFAULT_CHART_TYPE = 'individual';
const DEFAULT_CHART_UNIT = 'percentage';
const DEFAULT_CHART_GROUPING = Grouping.Daily;
const DEFAULT_CHART_MEASURE = 'delivery_metrics.opened';

export default class AnalysisController extends Controller {
  @service environment!: EnvironmentService;

  // I know this format looks weird, but that's what the Ember TS definitions
  // expect. Query params aren't typed well
  queryParams = [
    {
      dataTypesParam: { as: 'data_types' },
      tagIdsParam: { as: 'tag_ids' },
      name: { as: 'name' },
      excludeArchived: { as: 'exclude_archived' },
      excludeZeros: { as: 'exclude_zeros' },
      startTime: { as: 'start_time' },
      endTime: { as: 'end_time' },
      resolution: { as: 'resolution' },
      channelsParam: { as: 'channels' },
      measuresParam: { as: 'measures' },
      sortBy: { as: 'sort_by' },
      sortDirection: { as: 'sort_direction' },
      tab: { as: 'tab' },
      chartType: { as: 'chart_type' },
      chartUnit: { as: 'chart_unit' },
      chartGrouping: { as: 'chart_grouping' },
      chartMeasure: { as: 'chart_measure' },
    },
  ];

  // Ember generates pretty ugly query params for array types, so we do this
  // de/serialization to make it prettier
  // without our de/serializing: campaign%2Cnewsletter%2Cbroadcast
  // with de/serializing: campaign_newsletter_broadcast
  dataTypes = [...DEFAULT_DATA_TYPES];
  dataTypesParam: string = DEFAULT_DATA_TYPES.join('_');

  tagIds: string[] = [];
  tagIdsParam = '';

  name = '';

  excludeArchived = true;

  excludeZeros = false;

  startTime = '';
  endTime = '';

  resolution = Periods.DAYS;

  channels = [...DEFAULT_CHANNELS];
  channelsParam: string = DEFAULT_CHANNELS.join('_');

  measures = [...this.defaultMeasures];
  measuresParam: string = this.defaultMeasures.join('-');

  sortBy = DEFAULT_SORT_BY;
  sortDirection: SortDirection = DEFAULT_SORT_DIRECTION;

  tab = DEFAULT_TAB;

  chartType: 'individual' | 'aggregate' = 'individual';
  chartUnit: 'count' | 'percentage' = 'percentage';
  chartGrouping: Grouping = Grouping.Daily;
  chartMeasure = 'delivery_metrics.opened';

  get defaultMeasures() {
    if (this.environment.current.metricDenominator === 'delivered') {
      return [
        'delivery_metrics.sent',
        'delivery_metrics.delivered',
        'delivery_metrics.opened',
        'delivery_metrics.clicked',
        'delivery_metrics.unsubscribed',
      ];
    }
    return [
      'delivery_metrics.sent',
      'delivery_metrics.opened',
      'delivery_metrics.clicked',
      'delivery_metrics.unsubscribed',
    ];
  }

  @action
  resetQueryParams() {
    this.setProperties({
      dataTypes: [...DEFAULT_DATA_TYPES],
      tagIds: [],
      name: '',
      excludeArchived: true,
      excludeZeros: false,
      startTime: '',
      endTime: '',
      resolution: Periods.DAYS,
      channels: this.getAllAvailableChannels(),
      measures: [...this.defaultMeasures],
      sortBy: DEFAULT_SORT_BY,
      sortDirection: DEFAULT_SORT_DIRECTION,
      tab: DEFAULT_TAB,
      chartType: DEFAULT_CHART_TYPE,
      chartUnit: DEFAULT_CHART_UNIT,
      chartGrouping: DEFAULT_CHART_GROUPING,
      chartMeasure: DEFAULT_CHART_MEASURE,
    });

    this.serializeQueryParams();
  }

  @action
  resetMeasureQueryParams() {
    this.setProperties({
      measures: [...this.defaultMeasures],
      chartMeasure: isDeliveryMeasureEnabledForChannel('opened', 'email')
        ? DEFAULT_CHART_MEASURE
        : 'delivery_metrics.clicked',
    });

    this.serializeQueryParams();
  }

  @action
  getAllAvailableChannels() {
    return [...ALL_CHANNELS]
      .map((channel) => `${channel}_action`)
      .filter((type: ActionType) => this.environment.enabledAction(type))
      .map((type: ActionType) => actionTypeName(type));
  }

  @action
  setAllAvailableChannels() {
    if (this.channels.length === 0) {
      let availableChannels = this.getAllAvailableChannels();
      this.setProperties({
        channels: [...availableChannels],
        channelsParam: availableChannels.join('_'),
      });
    }
  }

  @action
  deserializeQueryParams() {
    let dataTypes = this.dataTypesParam === '' ? [] : this.dataTypesParam.split('_');
    let channels = this.channelsParam === '' ? [] : this.channelsParam.split('_');
    let measures = this.measuresParam === '' ? [] : this.measuresParam.split('-');

    this.setProperties({
      dataTypes: ALL_DATA_TYPES.filter((type) => dataTypes.includes(type)),
      tagIds: this.tagIdsParam === '' ? [] : this.tagIdsParam.split('_'),
      channels: ALL_CHANNELS.filter((channel) => channels.includes(channel)),
      measures: ALL_MEASURES.filter((measure) => measures.includes(measure)),
    });
  }

  @action
  serializeQueryParams() {
    this.setProperties({
      dataTypesParam: this.dataTypes.join('_'),
      tagIdsParam: [...this.tagIds].sort().join('_'),
      channelsParam: this.channels.join('_'),
      measuresParam: this.measures.join('-'),
    });
  }

  @action
  onDataTypesChange(value: AnalysisDataType) {
    // We set datatypes like this rather than directly to maintain the default
    // sort order
    if (this.dataTypes.includes(value)) {
      let dataTypes = this.dataTypes.filter((type) => type !== value);
      this.set(
        'dataTypes',
        ALL_DATA_TYPES.filter((type) => dataTypes.includes(type)),
      );
    } else {
      let dataTypes = [...this.dataTypes, value];
      this.set(
        'dataTypes',
        ALL_DATA_TYPES.filter((type) => dataTypes.includes(type)),
      );
    }

    this.serializeQueryParams();
  }

  @action
  onTagIdsChange(tagIds: string[]) {
    this.set('tagIds', tagIds);

    this.serializeQueryParams();
  }

  @action
  onTimeRangeChange(startTime: string, endTime: string) {
    this.setProperties({ startTime, endTime });
  }

  @action
  onChannelsChange(channels: AnalysisChannel) {
    // We set channels like this rather than directly to maintain the default
    // sort order
    this.set(
      'channels',
      [...channels].sort((a: AnalysisChannel, b: AnalysisChannel) => ALL_CHANNELS.indexOf(a) - ALL_CHANNELS.indexOf(b)),
    );

    this.serializeQueryParams();
  }

  @action
  onMeasuresChange(measures: string[]) {
    // We set measures like this rather than directly to maintain the default
    // sort order
    this.set(
      'measures',
      [...measures].sort((a, b) => ALL_MEASURES.indexOf(a) - ALL_MEASURES.indexOf(b)),
    );

    this.serializeQueryParams();
  }

  @action
  onSortChange(sortBy: string, sortDirection: SortDirection) {
    this.setProperties({
      sortBy,
      sortDirection,
    });
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your controllers.
declare module '@ember/controller' {
  interface Registry {
    analysis: AnalysisController;
  }
}
